import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { studentDetailsTransportReport } from "../../api/StudentDetails";
import { busStudentUpdate } from "../../api/Transport";
import { SweetAlertShow } from "../master/Master";
import StudentProfileHero from "../student/v5/components/StudentProfileHero";
import Master from "../student/Master";
import Nav from "../master/NavBar";
import Header from "../master/SideBar";

const MONTH_NAMES = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const monthName = (month) =>
    MONTH_NAMES[Number(month)] || "-";

const fmtMoney = (amount) =>
    `₹${Number(amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const fmtDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return "-";

    return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const toInputDate = (date) => {
    if (!date) return "";

    const value = String(date);

    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        return value.substring(0, 10);
    }

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return "";

    return `${d.getFullYear()}-${String(
        d.getMonth() + 1
    ).padStart(2, "0")}-${String(
        d.getDate()
    ).padStart(2, "0")}`;
};

const paymentStep = (type) => {
    if (type === "Quarterly") return 3;
    if (type === "Half Year") return 6;
    if (type === "Full Year") return 12;

    return 1;
};

const paymentTypeClass = (type) => {
    if (type === "Monthly")
        return "bg-label-primary";

    if (type === "Quarterly")
        return "bg-label-info";

    if (type === "Half Year")
        return "bg-label-warning";

    if (type === "Full Year")
        return "bg-label-success";

    return "bg-label-secondary";
};

export default function BusStudentedit() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const enrollmentId =
        searchParams.get("id");

    const studentId =
        searchParams.get("student_id");

    const [loading, setLoading] =
        useState(true);

    const [saving, setSaving] =
        useState(false);

    const [enrollment, setEnrollment] =
        useState(null);

    const [fees, setFees] =
        useState([]);

    const [deletedFeeIds, setDeletedFeeIds] =
        useState([]);

    const [editingFee, setEditingFee] =
        useState(null);

    const [form, setForm] = useState({
        joining_date: "",
        end_date: "",
        payment_type: "Monthly",
        discount: 0,
        discount_type: "Fixed",
        discount_remark: "",
    });

    useEffect(() => {
        loadData();
    }, [studentId, enrollmentId]);

    const loadData = async () => {
        if (!studentId || !enrollmentId) {
            setLoading(false);

            SweetAlertShow(
                "warning",
                "Missing Data",
                "Student ID or Enrollment ID is missing."
            );

            return;
        }

        try {
            setLoading(true);

            const response =
                await studentDetailsTransportReport(
                    studentId
                );

            if (!response?.data?.status) {
                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ||
                    "Unable to load data."
                );

                return;
            }

            const student =
                response.data.data;

            const enrollmentData = (
                student?.student_transport_n_fees ||
                []
            ).find(
                (item) =>
                    Number(item.id) ===
                    Number(enrollmentId)
            );

            if (!enrollmentData) {
                SweetAlertShow(
                    "info",
                    "Not Found",
                    "Bus enrollment not found."
                );

                return;
            }

            setEnrollment(enrollmentData);

            setFees(
                enrollmentData.bus_fee_details ||
                []
            );

            setDeletedFeeIds([]);

            setForm({
                joining_date:
                    toInputDate(
                        enrollmentData.joining_date
                    ),

                end_date:
                    toInputDate(
                        enrollmentData.end_date
                    ),

                payment_type:
                    enrollmentData.payment_type ||
                    "Monthly",

                discount: Number(
                    enrollmentData.total_discount ||
                    0
                ),

                discount_type: "Fixed",

                discount_remark:
                    enrollmentData.discount_remark ||
                    "",
            });
        } catch (error) {
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong!"
            );
        } finally {
            setLoading(false);
        }
    };

    const hasFees =
        fees.length > 0;

    /*
    |--------------------------------------------------------------------------
    | ACTUAL AMOUNT = STOP FEE
    |--------------------------------------------------------------------------
    */

    const busAmount = Number(
        enrollment?.stop?.stop_fee || 0
    );

    const formDiscount = Number(
        form.discount || 0
    );

    const formFinal = Math.max(
        0,
        busAmount - formDiscount
    );

    /*
    |--------------------------------------------------------------------------
    | TOTALS
    |--------------------------------------------------------------------------
    */

    const totals = useMemo(() => {
        return fees.reduce(
            (total, fee) => {
                const actual =
                    busAmount;

                const discount =
                    Number(
                        fee.discount || 0
                    );

                const final =
                    Math.max(
                        0,
                        actual - discount
                    );

                const paid =
                    Number(
                        fee.paid_amount || 0
                    );

                const due =
                    Math.max(
                        0,
                        final - paid
                    );

                total.actual += actual;
                total.discount += discount;
                total.final += final;
                total.paid += paid;
                total.due += due;

                return total;
            },
            {
                actual: 0,
                discount: 0,
                final: 0,
                paid: 0,
                due: 0,
            }
        );
    }, [fees, busAmount]);

    const paidCount =
        fees.filter(
            (fee) =>
                Number(
                    fee.paid_amount || 0
                ) > 0
        ).length;

    const unpaidCount =
        fees.filter(
            (fee) =>
                Number(
                    fee.paid_amount || 0
                ) === 0
        ).length;

    const changeForm = (
        field,
        value
    ) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    /*
    |--------------------------------------------------------------------------
    | EDIT FEE
    |--------------------------------------------------------------------------
    */

    const editFee = (fee) => {
        if (
            Number(
                fee.paid_amount || 0
            ) > 0
        ) {
            SweetAlertShow(
                "info",
                "Fee Locked",
                "Paid fee cannot be edited."
            );

            return;
        }

        const discount =
            Number(
                fee.discount || 0
            );

        const final =
            Math.max(
                0,
                busAmount - discount
            );

        const paid =
            Number(
                fee.paid_amount || 0
            );

        setEditingFee({
            ...fee,
            actual_amount:
                busAmount,
            discount,
            final_amount:
                final,
            due_amount:
                Math.max(
                    0,
                    final - paid
                ),
        });
    };

    /*
    |--------------------------------------------------------------------------
    | CHANGE FEE DISCOUNT
    |--------------------------------------------------------------------------
    */

    const changeFeeDiscount = (
        value
    ) => {
        if (!editingFee) return;

        if (value === "") {
            setEditingFee({
                ...editingFee,
                discount: "",
                actual_amount:
                    busAmount,
                final_amount:
                    busAmount,
                due_amount:
                    Math.max(
                        0,
                        busAmount -
                        Number(
                            editingFee.paid_amount ||
                            0
                        )
                    ),
            });

            return;
        }

        const discount =
            Number(value);

        if (discount < 0) return;

        if (
            discount >
            busAmount
        ) {
            return;
        }

        const paid =
            Number(
                editingFee.paid_amount ||
                0
            );

        const final =
            Math.max(
                0,
                busAmount -
                discount
            );

        const due =
            Math.max(
                0,
                final - paid
            );

        setEditingFee({
            ...editingFee,
            actual_amount:
                busAmount,
            discount,
            final_amount:
                final,
            due_amount:
                due,
        });
    };

    /*
    |--------------------------------------------------------------------------
    | SAVE FEE
    |--------------------------------------------------------------------------
    */

    const saveFee = () => {
        if (!editingFee) return;

        const actual =
            busAmount;

        const discount =
            Number(
                editingFee.discount || 0
            );

        if (discount < 0) {
            SweetAlertShow(
                "warning",
                "Invalid Discount",
                "Discount cannot be less than 0."
            );

            return;
        }

        if (
            discount >
            actual
        ) {
            SweetAlertShow(
                "warning",
                "Invalid Discount",
                "Discount cannot be greater than actual amount."
            );

            return;
        }

        const final =
            Math.max(
                0,
                actual - discount
            );

        const paid =
            Number(
                editingFee.paid_amount ||
                0
            );

        const due =
            Math.max(
                0,
                final - paid
            );

        setFees((prev) =>
            prev.map((fee) => {
                const sameFee =
                    editingFee.temp_id
                        ? fee.temp_id ===
                        editingFee.temp_id
                        : Number(
                            editingFee.id
                        ) ===
                        Number(
                            fee.id
                        );

                if (!sameFee)
                    return fee;

                return {
                    ...fee,
                    actual_amount:
                        actual,
                    discount,
                    final_amount:
                        final,
                    due_amount:
                        due,
                };
            })
        );

        setEditingFee(null);
    };

    /*
    |--------------------------------------------------------------------------
    | DELETE FEE
    |--------------------------------------------------------------------------
    */

    const deleteFee = (fee) => {
        if (
            Number(
                fee.paid_amount || 0
            ) > 0
        ) {
            SweetAlertShow(
                "info",
                "Fee Locked",
                "Paid fee cannot be deleted."
            );

            return;
        }

        if (fee.id) {
            setDeletedFeeIds(
                (prev) =>
                    prev.includes(
                        Number(fee.id)
                    )
                        ? prev
                        : [
                            ...prev,
                            Number(
                                fee.id
                            ),
                        ]
            );
        }

        setFees((prev) =>
            prev.filter(
                (item) => {
                    if (
                        fee.temp_id
                    ) {
                        return (
                            item.temp_id !==
                            fee.temp_id
                        );
                    }

                    return (
                        Number(
                            item.id
                        ) !==
                        Number(
                            fee.id
                        )
                    );
                }
            )
        );

        setEditingFee(null);
    };

    /*
    |--------------------------------------------------------------------------
    | ADD FEE
    |--------------------------------------------------------------------------
    */

    const addFee = () => {
        if (!form.joining_date) {
            SweetAlertShow(
                "warning",
                "Joining Date Required",
                "Please select joining date first."
            );

            return;
        }

        const step =
            paymentStep(
                form.payment_type
            );

        let date;

        if (fees.length > 0) {
            const lastFee =
                [...fees].sort(
                    (a, b) => {
                        const aDate =
                            new Date(
                                Number(
                                    a.year
                                ),
                                Number(
                                    a.month
                                ) - 1,
                                1
                            );

                        const bDate =
                            new Date(
                                Number(
                                    b.year
                                ),
                                Number(
                                    b.month
                                ) - 1,
                                1
                            );

                        return (
                            bDate -
                            aDate
                        );
                    }
                )[0];

            date =
                new Date(
                    Number(
                        lastFee.year
                    ),
                    Number(
                        lastFee.month
                    ) -
                    1 +
                    step,
                    1
                );
        } else {
            const [
                year,
                month,
                day,
            ] = form.joining_date
                .split("-")
                .map(Number);

            date =
                new Date(
                    year,
                    month - 1,
                    day
                );
        }

        if (
            Number.isNaN(
                date?.getTime()
            )
        ) {
            SweetAlertShow(
                "warning",
                "Invalid Date",
                "Please select a valid joining date."
            );

            return;
        }

        const month =
            date.getMonth() + 1;

        const year =
            date.getFullYear();

        const exists =
            fees.some(
                (fee) =>
                    Number(
                        fee.month
                    ) === month &&
                    Number(
                        fee.year
                    ) === year
            );

        if (exists) {
            SweetAlertShow(
                "info",
                "Already Exists",
                "This fee already exists."
            );

            return;
        }

        const newFee = {
            id: null,
            temp_id: `new_${Date.now()}`,

            month,
            year,

            type:
                form.payment_type,

            actual_amount:
                busAmount,

            discount: 0,

            final_amount:
                busAmount,

            paid_amount: 0,

            due_amount:
                busAmount,

            is_new: true,
        };

        setFees((prev) => [
            ...prev,
            newFee,
        ]);

        SweetAlertShow(
            "success",
            "Added",
            `${monthName(month)} ${year} fee added.`
        );
    };

    /*
    |--------------------------------------------------------------------------
    | UPDATE
    |--------------------------------------------------------------------------
    */

    const handleUpdate =
        async () => {
            const discount =
                Number(
                    form.discount || 0
                );

            if (
                !form.joining_date
            ) {
                SweetAlertShow(
                    "warning",
                    "Validation",
                    "Please select joining date."
                );

                return;
            }

            if (
                !form.payment_type
            ) {
                SweetAlertShow(
                    "warning",
                    "Validation",
                    "Please select payment type."
                );

                return;
            }

            if (
                discount < 0 ||
                discount >
                busAmount
            ) {
                SweetAlertShow(
                    "warning",
                    "Invalid Discount",
                    "Discount cannot be less than 0 or greater than actual amount."
                );

                return;
            }


            if (editingFee) {
                SweetAlertShow(
                    "info",
                    "Save Fee First",
                    "Please save or cancel the fee currently being edited."
                );

                return;
            }

            try {
                setSaving(true);

                const data = {
                    enrollment_id:
                        enrollmentId,

                    student_id:
                        studentId,

                    joining_date:
                        form.joining_date,

                    end_date:
                        form.end_date ||
                        null,

                    old_payment_type:
                        enrollment?.payment_type ||
                        "",

                    new_payment_type:
                        form.payment_type,

                    actual_amount:
                        busAmount,

                    discount_type:
                        form.discount_type,

                    discount,

                    discount_remark:
                        discount > 0
                            ? form.discount_remark.trim()
                            : null,

                    deleted_fee_ids:
                        deletedFeeIds,

                    fees: fees.map(
                        (fee) => {
                            const actual =
                                busAmount;

                            const feeDiscount =
                                Number(
                                    fee.discount ||
                                    0
                                );

                            const final =
                                Math.max(
                                    0,
                                    actual -
                                    feeDiscount
                                );

                            const paid =
                                Number(
                                    fee.paid_amount ||
                                    0
                                );

                            const due =
                                Math.max(
                                    0,
                                    final -
                                    paid
                                );

                            return {
                                id:
                                    fee.id ||
                                    null,

                                month:
                                    Number(
                                        fee.month
                                    ),

                                year:
                                    Number(
                                        fee.year
                                    ),

                                type:
                                    fee.type ||
                                    form.payment_type,

                                actual_amount:
                                    actual,

                                discount:
                                    feeDiscount,

                                final_amount:
                                    final,

                                paid_amount:
                                    paid,

                                due_amount:
                                    due,
                            };
                        }
                    ),
                };

                const response =
                    await busStudentUpdate(
                        data
                    );

                if (
                    !response?.data
                        ?.status
                ) {
                    throw new Error(
                        response?.data
                            ?.message ||
                        "Unable to update fees."
                    );
                }

                SweetAlertShow(
                    "success",
                    "Updated",
                    response?.data
                        ?.message ||
                    "Bus fees updated successfully."
                );

                await loadData();
            } catch (error) {
                SweetAlertShow(
                    "error",
                    "Error",
                    error?.response?.data
                        ?.message ||
                    error?.message ||
                    "Something went wrong!"
                );
            } finally {
                setSaving(false);
            }
        };

    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (loading) {
        return (
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <Header />

                    <div className="layout-page">
                        <Nav />

                        <div className="content-wrapper">
                            <div className="container-fluid container-p-y">
                                <div className="card">
                                    <div className="card-body text-center py-5">
                                        <div className="spinner-border text-primary mb-3" />

                                        <h5>
                                            Loading Bus Fee...
                                        </h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!enrollment) {
        return (
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <Header />

                    <div className="layout-page">
                        <Nav />

                        <div className="content-wrapper">
                            <div className="container-fluid container-p-y">
                                <div className="card">
                                    <div className="card-body text-center py-5">
                                        <i className="ri-bus-line text-danger fs-1" />

                                        <h4>
                                            Bus Enrollment Not Found
                                        </h4>

                                        <button
                                            className="btn btn-primary"
                                            onClick={() =>
                                                navigate(
                                                    -1
                                                )
                                            }
                                        >
                                            Go Back
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <Header />

                <div className="layout-page">
                    <Nav />

                    <div className="content-wrapper">
                        <div className="container-fluid container-p-y">
                            <div className="row">
                                <Master />

                                <div className="col-12">
                                    <StudentProfileHero
                                        id={
                                            studentId
                                        }
                                    />

                                    {/* HEADER */}
                                    <div
                                        className="card border-0 shadow-sm mb-4 overflow-hidden"
                                        style={{
                                            borderRadius:
                                                "18px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                background:
                                                    "linear-gradient(135deg,#5b4bd8,#8f55e8)",
                                                color: "#fff",
                                                padding:
                                                    "25px",
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <button
                                                    className="btn rounded-circle"
                                                    style={{
                                                        width: 45,
                                                        height: 45,
                                                        background:
                                                            "rgba(255,255,255,.18)",
                                                        color: "#fff",
                                                    }}
                                                    onClick={() =>
                                                        navigate(
                                                            -1
                                                        )
                                                    }
                                                >
                                                    <i className="ri-arrow-left-line" />
                                                </button>

                                                <div>
                                                    <div className="small">
                                                        TRANSPORT FEE MANAGEMENT
                                                    </div>

                                                    <h3 className="text-white fw-bold mb-1">
                                                        Bus Fee Update
                                                    </h3>

                                                    <small>
                                                        Enrollment #
                                                        {
                                                            enrollment.id
                                                        }
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* SUMMARY */}
                                    <div className="row g-3 mb-4">
                                        <div className="col-xl-3 col-md-6">
                                            <div className="card border-0 shadow-sm h-100">
                                                <div className="card-body">
                                                    <small className="text-muted">
                                                        BUS NUMBER
                                                    </small>

                                                    <h5 className="fw-bold mt-2">
                                                        {
                                                            enrollment
                                                                ?.bus
                                                                ?.bus_number ||
                                                            "-"
                                                        }
                                                    </h5>

                                                    <small className="text-muted">
                                                        {
                                                            enrollment
                                                                ?.bus
                                                                ?.driver_name ||
                                                            "-"
                                                        }
                                                    </small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-xl-3 col-md-6">
                                            <div className="card border-0 shadow-sm h-100">
                                                <div className="card-body">
                                                    <small className="text-muted">
                                                        BUS STOP
                                                    </small>

                                                    <h5 className="fw-bold mt-2">
                                                        {
                                                            enrollment
                                                                ?.stop
                                                                ?.stop_name ||
                                                            "-"
                                                        }
                                                    </h5>

                                                    <small className="text-muted">
                                                        Stop Fee:{" "}
                                                        {fmtMoney(
                                                            enrollment
                                                                ?.stop
                                                                ?.stop_fee
                                                        )}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-xl-3 col-md-6">
                                            <div className="card border-0 shadow-sm h-100">
                                                <div className="card-body">
                                                    <small className="text-muted">
                                                        JOINING DATE
                                                    </small>

                                                    <h5 className="fw-bold mt-2">
                                                        {fmtDate(
                                                            enrollment.joining_date
                                                        )}
                                                    </h5>

                                                    {hasFees ? (
                                                        <small className="text-danger">
                                                            <i className="ri-lock-line me-1" />
                                                            Locked
                                                        </small>
                                                    ) : (
                                                        <small className="text-success">
                                                            Editable
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-xl-3 col-md-6">
                                            <div className="card border-0 shadow-sm h-100">
                                                <div className="card-body">
                                                    <small className="text-muted">
                                                        PAYMENT TYPE
                                                    </small>

                                                    <h5 className="fw-bold mt-2">
                                                        {
                                                            enrollment.payment_type
                                                        }
                                                    </h5>

                                                    <span
                                                        className={`badge ${paymentTypeClass(
                                                            enrollment.payment_type
                                                        )}`}
                                                    >
                                                        {
                                                            enrollment.payment_type
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* FEE SETTINGS */}
                                    <div
                                        className="card border-0 shadow-sm mb-4"
                                        style={{ borderRadius: "18px" }}
                                    >
                                        <div className="card-header bg-white border-0 p-4">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="small text-primary fw-bold">
                                                        FEE SETTINGS
                                                    </div>

                                                    <h5 className="fw-bold mb-0">
                                                        Update Fee
                                                    </h5>
                                                </div>

                                                {hasFees && (
                                                    <span className="badge bg-label-danger">
                                                        <i className="ri-lock-line me-1" />
                                                        Date & Type Locked
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="card-body">
                                            <div className="row g-3">

                                                {/* JOINING DATE */}
                                                <div className="col-md-3">
                                                    <label className="form-label fw-semibold">
                                                        Joining Date
                                                    </label>

                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={form.joining_date}
                                                        disabled={hasFees}
                                                        onChange={(e) =>
                                                            changeForm(
                                                                "joining_date",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>

                                                {/* PAYMENT TYPE */}
                                                <div className="col-md-3">
                                                    <label className="form-label fw-semibold">
                                                        Payment Type
                                                    </label>

                                                    <select
                                                        className="form-select"
                                                        value={form.payment_type}
                                                        disabled={hasFees}
                                                        onChange={(e) =>
                                                            changeForm(
                                                                "payment_type",
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <option value="Monthly">
                                                            Monthly
                                                        </option>

                                                        <option value="Quarterly">
                                                            Quarterly
                                                        </option>

                                                        <option value="Half Year">
                                                            Half Year
                                                        </option>

                                                        <option value="Full Year">
                                                            Full Year
                                                        </option>
                                                    </select>
                                                </div>

                                                {/* BUS STOP FEE - ONLY SHOW */}
                                                <div className="col-md-3">
                                                    <label className="form-label fw-semibold">
                                                        Bus Stop Fee
                                                    </label>

                                                    <div className="input-group">
                                                        <span className="input-group-text">
                                                            ₹
                                                        </span>

                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={
                                                                enrollment?.stop?.stop_fee || 0
                                                            }
                                                            disabled
                                                        />
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>

                                    {/* FEE SCHEDULE */}
                                    <div
                                        className="card border-0 shadow-sm mb-4"
                                        style={{
                                            borderRadius:
                                                "18px",
                                        }}
                                    >
                                        <div className="card-header bg-white border-0 p-4">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <div>
                                                    <div className="small text-primary fw-bold">
                                                        FEE SCHEDULE
                                                    </div>

                                                    <h4 className="fw-bold mb-0">
                                                        Bus Fees
                                                    </h4>

                                                    <small className="text-muted">
                                                        {
                                                            paidCount
                                                        }{" "}
                                                        Paid ·{" "}
                                                        {
                                                            unpaidCount
                                                        }{" "}
                                                        Unpaid
                                                    </small>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={
                                                        addFee
                                                    }
                                                >
                                                    <i className="ri-add-line me-1" />
                                                    Add Fee
                                                </button>
                                            </div>
                                        </div>

                                        <div className="card-body">
                                            {fees.length ===
                                                0 ? (
                                                <div className="text-center py-4">
                                                    <i className="ri-file-list-3-line fs-1 text-primary" />

                                                    <h5 className="mt-2">
                                                        No Fee Added
                                                    </h5>

                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={
                                                            addFee
                                                        }
                                                    >
                                                        <i className="ri-add-line me-1" />
                                                        Add Fee
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="d-flex flex-column gap-3">
                                                    {fees.map(
                                                        (
                                                            fee
                                                        ) => {
                                                            const paid =
                                                                Number(
                                                                    fee.paid_amount ||
                                                                    0
                                                                ) >
                                                                0;

                                                            const editing =
                                                                editingFee &&
                                                                (fee.temp_id
                                                                    ? editingFee.temp_id ===
                                                                    fee.temp_id
                                                                    : Number(
                                                                        editingFee.id
                                                                    ) ===
                                                                    Number(
                                                                        fee.id
                                                                    ));

                                                            const actual =
                                                                busAmount;

                                                            const discount =
                                                                editing
                                                                    ? Number(
                                                                        editingFee.discount ||
                                                                        0
                                                                    )
                                                                    : Number(
                                                                        fee.discount ||
                                                                        0
                                                                    );

                                                            const final =
                                                                Math.max(
                                                                    0,
                                                                    actual -
                                                                    discount
                                                                );

                                                            const paidAmount =
                                                                Number(
                                                                    fee.paid_amount ||
                                                                    0
                                                                );

                                                            const due =
                                                                Math.max(
                                                                    0,
                                                                    final -
                                                                    paidAmount
                                                                );

                                                            return (
                                                                <div
                                                                    key={
                                                                        fee.temp_id ||
                                                                        fee.id ||
                                                                        `${fee.month}-${fee.year}`
                                                                    }
                                                                    className="border rounded-4 p-3"
                                                                >
                                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                                        <div>
                                                                            <h5 className="fw-bold mb-1">
                                                                                {monthName(
                                                                                    fee.month
                                                                                )}{" "}
                                                                                {
                                                                                    fee.year
                                                                                }
                                                                            </h5>

                                                                            <span
                                                                                className={`badge ${paymentTypeClass(
                                                                                    fee.type
                                                                                )}`}
                                                                            >
                                                                                {
                                                                                    fee.type
                                                                                }
                                                                            </span>
                                                                        </div>

                                                                        {paid ? (
                                                                            <span className="badge bg-success">
                                                                                <i className="ri-lock-line me-1" />
                                                                                PAID
                                                                            </span>
                                                                        ) : (
                                                                            <span className="badge bg-warning text-dark">
                                                                                UNPAID
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <div className="row g-3 align-items-end">

                                                                        {/* ACTUAL */}
                                                                        <div className="col-md-2">
                                                                            <small className="text-muted">
                                                                                ACTUAL
                                                                            </small>

                                                                            <h6 className="fw-bold mt-1">
                                                                                {fmtMoney(
                                                                                    actual
                                                                                )}
                                                                            </h6>
                                                                        </div>

                                                                        {/* DISCOUNT */}
                                                                        <div className="col-md-2">
                                                                            <small className="text-muted">
                                                                                DISCOUNT
                                                                            </small>

                                                                            {editing ? (
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    max={
                                                                                        busAmount
                                                                                    }
                                                                                    className="form-control"
                                                                                    value={
                                                                                        editingFee.discount
                                                                                    }
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        changeFeeDiscount(
                                                                                            e
                                                                                                .target
                                                                                                .value
                                                                                        )
                                                                                    }
                                                                                />
                                                                            ) : (
                                                                                <h6 className="fw-bold text-warning mt-1">
                                                                                    {fmtMoney(
                                                                                        fee.discount
                                                                                    )}
                                                                                </h6>
                                                                            )}
                                                                        </div>

                                                                        {/* FINAL */}
                                                                        <div className="col-md-2">
                                                                            <small className="text-muted">
                                                                                FINAL
                                                                            </small>

                                                                            <h6 className="fw-bold text-primary mt-1">
                                                                                {fmtMoney(
                                                                                    final
                                                                                )}
                                                                            </h6>
                                                                        </div>

                                                                        {/* PAID */}
                                                                        <div className="col-md-2">
                                                                            <small className="text-muted">
                                                                                PAID
                                                                            </small>

                                                                            <h6 className="fw-bold text-success mt-1">
                                                                                {fmtMoney(
                                                                                    paidAmount
                                                                                )}
                                                                            </h6>
                                                                        </div>

                                                                        {/* DUE */}
                                                                        <div className="col-md-2">
                                                                            <small className="text-muted">
                                                                                DUE
                                                                            </small>

                                                                            <h6 className="fw-bold text-danger mt-1">
                                                                                {fmtMoney(
                                                                                    due
                                                                                )}
                                                                            </h6>
                                                                        </div>

                                                                        {/* ACTION */}
                                                                        <div className="col-md-2">
                                                                            {paid ? (
                                                                                <button
                                                                                    className="btn btn-light w-100"
                                                                                    disabled
                                                                                >
                                                                                    <i className="ri-lock-line me-1" />
                                                                                    Locked
                                                                                </button>
                                                                            ) : editing ? (
                                                                                <div className="d-flex gap-1">
                                                                                    <button
                                                                                        className="btn btn-success"
                                                                                        onClick={
                                                                                            saveFee
                                                                                        }
                                                                                    >
                                                                                        <i className="ri-check-line" />
                                                                                    </button>

                                                                                    <button
                                                                                        className="btn btn-secondary"
                                                                                        onClick={() =>
                                                                                            setEditingFee(
                                                                                                null
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <i className="ri-close-line" />
                                                                                    </button>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="d-flex gap-1">
                                                                                    <button
                                                                                        className="btn btn-outline-primary"
                                                                                        onClick={() =>
                                                                                            editFee(
                                                                                                fee
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <i className="ri-edit-line" />
                                                                                    </button>

                                                                                    <button
                                                                                        className="btn btn-outline-danger"
                                                                                        onClick={() =>
                                                                                            deleteFee(
                                                                                                fee
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <i className="ri-delete-bin-line" />
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {fees.length >
                                            0 && (
                                                <div className="card-footer bg-white">
                                                    <div className="row g-3">
                                                        <div className="col">
                                                            <small className="text-muted">
                                                                ACTUAL
                                                            </small>

                                                            <div className="fw-bold">
                                                                {fmtMoney(
                                                                    totals.actual
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="col">
                                                            <small className="text-muted">
                                                                DISCOUNT
                                                            </small>

                                                            <div className="fw-bold text-warning">
                                                                {fmtMoney(
                                                                    totals.discount
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="col">
                                                            <small className="text-muted">
                                                                FINAL
                                                            </small>

                                                            <div className="fw-bold text-primary">
                                                                {fmtMoney(
                                                                    totals.final
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="col">
                                                            <small className="text-muted">
                                                                PAID
                                                            </small>

                                                            <div className="fw-bold text-success">
                                                                {fmtMoney(
                                                                    totals.paid
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="col">
                                                            <small className="text-muted">
                                                                DUE
                                                            </small>

                                                            <div className="fw-bold text-danger">
                                                                {fmtMoney(
                                                                    totals.due
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                    </div>

                                    {/* SAVE */}
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <div>
                                                    <h6 className="fw-bold mb-1">
                                                        Fee records are protected
                                                    </h6>

                                                    <small className="text-muted">
                                                        Paid fee कभी edit या
                                                        delete नहीं होगी।
                                                    </small>
                                                </div>

                                                <div className="d-flex gap-2">
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        disabled={
                                                            saving
                                                        }
                                                        onClick={() =>
                                                            navigate(
                                                                -1
                                                            )
                                                        }
                                                    >
                                                        Cancel
                                                    </button>

                                                    <button
                                                        className="btn btn-primary"
                                                        disabled={
                                                            saving
                                                        }
                                                        onClick={
                                                            handleUpdate
                                                        }
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" />
                                                                Saving...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-save-line me-1" />
                                                                Update Fees
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}